// Impresión de documentos operacionales (manifiesto, hoja de rendición, NV, OC…). El documento se
// dibuja en una zona propia y el navegador imprime SOLO esa zona (CSS @media print). Sirve para papel
// y para «Guardar como PDF». Individual o masiva: se le pasan uno o varios documentos.
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Dibuja `children` en la zona de impresión, imprime y llama `alTerminar`. `horizontal`: la hoja sale
 * apaisada sin que haya que elegirlo en el diálogo (se fija el tamaño de página solo mientras imprime).
 */
export function Imprimir({
  children,
  alTerminar,
  horizontal = false,
}: {
  children: ReactNode;
  alTerminar: () => void;
  horizontal?: boolean;
}) {
  useEffect(() => {
    document.body.classList.add('imprimiendo');
    const pagina = horizontal ? document.createElement('style') : null;
    if (pagina) {
      pagina.textContent = '@page { size: A4 landscape; margin: 10mm; }';
      document.head.appendChild(pagina);
    }
    // Un tick para que el portal pinte antes de abrir el diálogo de impresión.
    const t = window.setTimeout(() => {
      try {
        window.print();
      } finally {
        document.body.classList.remove('imprimiendo');
        pagina?.remove();
        alTerminar();
      }
    }, 50);
    return () => {
      window.clearTimeout(t);
      document.body.classList.remove('imprimiendo');
      pagina?.remove();
    };
  }, [alTerminar, horizontal]);
  return createPortal(
    <div className="zona-impresion" aria-hidden>
      {children}
    </div>,
    document.body,
  );
}

/** Encabezado común de un documento impreso: empresa, título, número y fecha de impresión. */
export function EncabezadoImpreso({
  empresa,
  titulo,
  numero,
  detalle,
}: {
  empresa: string;
  titulo: string;
  numero: string;
  detalle?: ReactNode;
}) {
  return (
    <header className="impreso-encabezado">
      <div>
        <div className="impreso-empresa">{empresa}</div>
        <div className="impreso-titulo">{titulo}</div>
      </div>
      <div className="impreso-numero">
        <div>{numero}</div>
        <div className="impreso-fecha">
          Impreso el{' '}
          {new Date().toLocaleString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
      {detalle && <div className="impreso-detalle">{detalle}</div>}
    </header>
  );
}
